import * as z from 'zod';
import { ErrorKeys } from '@/@shared/types/errorKeys';

/**
 * 이메일 검증
 * - 필수 입력
 * - 이메일 형식 검증
 */
const email = z
	.string({
		required_error: ErrorKeys.email.required,
	})
	.min(1, {
		message: ErrorKeys.email.required,
	})
	.email({
		message: ErrorKeys.email.regexError,
	})
	.trim();

/**
 * 비밀번호 검증
 * - 필수 입력
 * - 8~20자
 * - 영문/숫자/특수문자 3가지 모두 조합
 * - 공백 불가
 * - 특수문자 제한: ( ) < > ' " ; 사용 불가
 */
const password = z
	.string({
		required_error: ErrorKeys.password.required,
	})
	.min(8, {
		message: ErrorKeys.password.lengthError,
	})
	.max(20, {
		message: ErrorKeys.password.lengthError,
	})
	.superRefine((val, ctx) => {
		// 공백 체크
		if (/\s/.test(val)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: ErrorKeys.password.whitespaceError,
			});
			return;
		}

		// 금지 문자 체크
		if (/[()<>'\";]/.test(val)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: ErrorKeys.password.regexError,
			});
			return;
		}

		// 영문/숫자/특수문자 조합 체크
		const hasLetter = /[a-zA-Z]/.test(val);
		const hasNumber = /\d/.test(val);
		const hasSpecialChar = /[@$!%*?&#^-]/.test(val);

		if (!hasLetter || !hasNumber || !hasSpecialChar) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: ErrorKeys.password.combinationError,
			});
		}
	});

/**
 * 비밀번호 확인
 * - 필수 입력
 */
const confirmPassword = z
	.string({
		required_error: ErrorKeys.confirmPassword.required,
	})
	.min(1, {
		message: ErrorKeys.confirmPassword.required,
	});

/**
 * 이메일 인증 코드
 * - 정확히 6자리
 * - 숫자만 허용
 */
const authCode = z
	.string({
		required_error: ErrorKeys.authCode.required,
	})
	.length(6, {
		message: ErrorKeys.authCode.lengthError,
	})
	.regex(/^\d{6}$/, {
		message: ErrorKeys.authCode.formatError,
	});

/**
 * 약관 동의
 * - boolean 타입
 */
const policy = z.boolean({
	required_error: 'required',
});

/**
 * 로그인 스키마
 * - 이메일 + 비밀번호
 */
export const signInSchema = z.object({
	email,
	password,
});

/**
 * 약관 동의 스키마
 * - 서비스 이용약관 (필수)
 * - 개인정보 처리방침 (필수)
 * - 14세 이상 (필수)
 * - 마케팅 수신 동의 (선택)
 */
const agreements = z.object({
	stepin: policy.refine((val) => val === true, {
		message: ErrorKeys.agreements.stepinRequired,
	}),
	privacy: policy.refine((val) => val === true, {
		message: ErrorKeys.agreements.privacyRequired,
	}),
	age14: policy.refine((val) => val === true, {
		message: ErrorKeys.agreements.age14Required,
	}),
	marketing: policy, // 선택 항목
});

/**
 * 비밀번호 + 비밀번호 확인
 * - 두 비밀번호가 일치해야 함
 */
export const passwords = z
	.object({
		getPassword: password,
		confirmPassword: confirmPassword,
	})
	.refine(
		(values) => {
			return values.getPassword === values.confirmPassword;
		},
		{
			message: ErrorKeys.confirmPassword.confirmError,
			path: ['confirmPassword'],
		}
	);

/**
 * 회원가입용 이메일 (getEmail)
 */
const getEmailSchema = z
	.string({
		required_error: ErrorKeys.email.required,
	})
	.min(1, {
		message: ErrorKeys.email.required,
	})
	.email({
		message: ErrorKeys.email.regexError,
	})
	.trim();

/**
 * 회원가입용 비밀번호 (getPassword)
 * - 필수 입력
 * - 8~20자
 * - 영문/숫자/특수문자 3가지 모두 조합
 * - 공백 불가
 * - 특수문자 제한: ( ) < > ' " ; 사용 불가
 */
const getPasswordSchema = z
	.string({
		required_error: ErrorKeys.password.required,
	})
	.min(8, {
		message: ErrorKeys.password.lengthError,
	})
	.max(20, {
		message: ErrorKeys.password.lengthError,
	})
	.superRefine((val, ctx) => {
		// 공백 체크
		if (/\s/.test(val)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: ErrorKeys.password.whitespaceError,
			});
			return;
		}

		// 금지 문자 체크
		if (/[()<>'\";]/.test(val)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: ErrorKeys.password.regexError,
			});
			return;
		}

		// 영문/숫자/특수문자 조합 체크
		const hasLetter = /[a-zA-Z]/.test(val);
		const hasNumber = /\d/.test(val);
		const hasSpecialChar = /[@$!%*?&#^-]/.test(val);

		if (!hasLetter || !hasNumber || !hasSpecialChar) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: ErrorKeys.password.combinationError,
			});
		}
	});

/**
 * 회원가입 스키마
 * - 이메일
 * - 이메일 인증
 * - 약관 동의
 * - 비밀번호
 * - 인증 상태
 */
export const signUpSchema = z.object({
	getEmail: getEmailSchema,
	verifyEmail: authCode,
	verifyStatus: z.boolean({
		required_error: ErrorKeys.authCode.verifyError,
	}),
	registerAgreements: agreements,
	getPasswords: z
		.object({
			getPassword: getPasswordSchema,
			confirmPassword: confirmPassword,
		})
		.refine(
			(values) => {
				return values.getPassword === values.confirmPassword;
			},
			{
				message: ErrorKeys.confirmPassword.confirmError,
				path: ['confirmPassword'],
			}
		),
});

// 타입 export
export type SignInType = z.infer<typeof signInSchema>;
export type SignUpType = z.infer<typeof signUpSchema>;
export type AgreementsType = z.infer<typeof agreements>;
export type PasswordsType = z.infer<typeof passwords>;
